import { useState } from 'react';
import type {
    StatusShift,
    ClockRole,
    UserStatusAction,
    PunchResponse
} from '@src/api/punchTypes';
import { Spinner } from '@7shifts/sous-chef-base';
import {
    formatLocalTime,
    formatDuration
} from '@src/components/PunchPad/timeUtils';
import { StartShiftIcon } from './icons/StartShiftIcon';
import { BreakIcon } from './icons/BreakIcon';
import { StartShiftRoleModal } from './StartShiftRoleModal';
import './ShiftCard.css';

const ACTION_ICONS: Record<string, React.ReactNode> = {
    'Start shift': <StartShiftIcon />,
    'Start unscheduled shift': <StartShiftIcon />,
    'Clock Out': <StartShiftIcon />,
    'Start Break': <BreakIcon />,
    'Start Paid Break': <BreakIcon />,
    'End Break': <BreakIcon />
};

type ButtonTheme = 'primary' | 'hollow' | 'danger';

const ACTION_THEME: Record<string, ButtonTheme> = {
    'Clock Out': 'danger',
    'Start Break': 'hollow',
    'Start Paid Break': 'hollow',
    'End Break': 'hollow'
};

const ICON_BEFORE = new Set(['Start Break', 'Start Paid Break', 'End Break']);

interface ShiftCardProps {
    shift: StatusShift;
    availableActions: UserStatusAction[];
    loadingLabel: string | null;
    locationTimezone: string;
    onAction: (execute: () => Promise<PunchResponse>, label: string) => void;
    otherAssignedRoles?: ClockRole[];
}

export function ShiftCard({
    shift,
    availableActions,
    loadingLabel,
    locationTimezone,
    onAction,
    otherAssignedRoles = []
}: ShiftCardProps) {
    const submitted = loadingLabel !== null;
    const [pendingStartShift, setPendingStartShift] =
        useState<UserStatusAction | null>(null);
    const timeRange = `${formatLocalTime(
        shift.startTime,
        locationTimezone
    )} - ${formatLocalTime(shift.endTime, locationTimezone)}`;
    const duration = formatDuration(shift.startTime, shift.endTime);
    const initial = (shift.roleName?.[0] ?? '?').toUpperCase();
    const details = [shift.departmentName, shift.roleName]
        .filter(Boolean)
        .join(' | ');

    return (
        <div className="shift-card" data-testid="shift-card">
            {pendingStartShift && (
                <StartShiftRoleModal
                    shift={shift}
                    otherRoles={otherAssignedRoles}
                    action={pendingStartShift}
                    onAction={execute => {
                        setPendingStartShift(null);
                        onAction(execute, pendingStartShift.label);
                    }}
                    onDismiss={() => setPendingStartShift(null)}
                />
            )}
            <div className="shift-card__header">
                <div
                    className="shift-card__role-avatar"
                    style={{
                        backgroundColor:
                            shift.color ??
                            'var(--color-surface-on-color-subtle)'
                    }}
                    aria-hidden="true"
                >
                    <span className="shift-card__role-initial">{initial}</span>
                </div>
                <div className="shift-card__info">
                    <p className="shift-card__time-range">
                        <span>{timeRange}</span>
                        <span className="shift-card__duration">
                            {' '}
                            ({duration})
                        </span>
                    </p>
                    {details && <p className="shift-card__detail">{details}</p>}
                </div>
            </div>
            {availableActions.length > 0 && (
                <div className="shift-card__actions">
                    {availableActions.map(action => {
                        const theme = ACTION_THEME[action.label] ?? 'primary';
                        const icon = ACTION_ICONS[action.label];
                        const iconBefore = ICON_BEFORE.has(action.label);
                        return (
                            <button
                                key={action.label}
                                className={`shift-card__action-btn shift-card__action-btn--${theme}`}
                                type="button"
                                aria-label={action.label}
                                disabled={submitted}
                                onClick={() => {
                                    if (
                                        action.type === 'start_shift' &&
                                        action.executeWithRole &&
                                        otherAssignedRoles.length > 0
                                    ) {
                                        setPendingStartShift(action);
                                    } else {
                                        onAction(action.execute, action.label);
                                    }
                                }}
                            >
                                {loadingLabel === action.label ? (
                                    <Spinner size={16} theme="contrast" />
                                ) : (
                                    <>
                                        {iconBefore && icon}
                                        {action.label}
                                        {!iconBefore && icon}
                                    </>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
